/**
 * The CSRF token, fetched rather than printed.
 *
 * Two problems, one endpoint.
 *
 * A tab left open past the session lifetime came back with a token the server
 * had forgotten. The next request answered 419, and the page told the visitor
 * their session had expired and left them to reload by hand, losing whatever
 * was in the editor. Now the token is refetched and the request repeated once.
 *
 * And a token printed into the HTML is what is left making these pages personal
 * documents: the same markup handed to two visitors would hand them one
 * another's token. The pages ship without one and ask for it here, which is
 * what lets an anonymous GET be answered from the edge.
 *
 * The catch worth knowing about: when a session dies, the token that replaces
 * it belongs to a NEW, signed-out session. Repeating a signed-in request with
 * it would quietly succeed as a guest — a different word allowance, and nothing
 * written to the account's history. So the endpoint reports who it thinks is
 * asking, and a request that was signed in refuses to be retried as a stranger.
 */
(function () {
    'use strict';

    var ENDPOINT = '/csrf-token';
    /* A tab hidden longer than this may have outlived its session, so it gets a
       fresh token before the visitor touches anything — that way the first
       click works rather than costing a round trip and a retry. */
    var STALE_AFTER_MS = 5 * 60 * 1000;

    var meta = document.querySelector('meta[name="csrf-token"]');
    var pending = null;
    var hiddenAt = 0;

    /* A cookie-less Safari tab cannot prove ownership of an asynchronous job
       with Laravel's session. Give guest tabs a high-entropy bearer instead.
       The server stores only an HMAC of this value and ignores it whenever a
       valid guest cookie or authenticated session exists. */
    function createGuestAccessToken() {
        if (window.IS_LOGGED_IN || !window.crypto || !window.crypto.getRandomValues || !window.btoa) {
            return '';
        }

        var bytes = new Uint8Array(32);
        window.crypto.getRandomValues(bytes);
        var binary = '';
        for (var i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);

        return window.btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/g, '');
    }

    var guestAccessToken = createGuestAccessToken();
    window.guestAccessToken = guestAccessToken;

    function apply(token) {
        if (!token) return token;
        if (meta) meta.setAttribute('content', token);
        document.querySelectorAll('input[name="_token"]').forEach(function (input) {
            input.value = token;
        });
        return token;
    }

    function token() {
        return (meta && meta.getAttribute('content')) || '';
    }

    // Concurrent callers share one request rather than racing for a token each.
    function refresh() {
        if (pending) return pending;

        pending = window.fetch(ENDPOINT, {
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' }
        })
            .then(function (response) { return response.json(); })
            .then(function (data) {
                apply(data.token);
                pending = null;
                return data;
            })
            .catch(function (error) {
                pending = null;
                throw error;
            });

        return pending;
    }

    /* Pages that ship without a token — the public ones, the cacheable ones —
       start the request immediately, so it is in flight while the rest of the
       page is still parsing. Anything that posts on load waits on this. */
    var ready = token()
        ? Promise.resolve({ token: token(), authenticated: !!window.IS_LOGGED_IN })
        : refresh().catch(function () { return { token: '', authenticated: false }; });

    function sessionExpired() {
        var error = new Error('The session has expired.');
        error.sessionExpired = true;
        document.dispatchEvent(new CustomEvent('csrf:session-expired'));
        return error;
    }

    /**
     * fetch() that carries the token and survives it going stale.
     * The body is sent again as given, so pass a string or a FormData that can
     * be replayed — not a stream.
     */
    function csrfFetch(url, options) {
        var opts = options || {};

        return ready.then(function () {
            return send(false);
        });

        function send(isRetry) {
            /* Accept matters as much as the token here: Laravel answers a stale
               one with a redirect unless the caller asked for JSON, and a
               redirect back to the page returns 200 with a document — which
               reads as success and breaks on .json(). */
            var headers = Object.assign({ 'Accept': 'application/json' }, opts.headers, {
                'X-CSRF-TOKEN': token()
            });

            if (guestAccessToken) headers['X-Guest-Access-Token'] = guestAccessToken;

            /* What the page believes about itself. The tools take guests, so a
               request that arrives without a session runs perfectly happily —
               on the guest allowance, and never reaching the account's history.
               Saying so lets the server disagree (EnsureExpectedSession), which
               is the only way to catch a session that ended without the token
               noticing: a second press after the token was refreshed, or an
               account deleted in another tab, which leaves cookie and token
               both valid. */
            if (window.IS_LOGGED_IN) headers['X-Expect-Auth'] = '1';

            // A FormData body carries its own copy of the token.
            if (opts.body instanceof FormData && opts.body.has('_token')) {
                opts.body.set('_token', token());
            }

            return window.fetch(url, Object.assign({}, opts, {
                credentials: opts.credentials || 'same-origin',
                headers: headers
            })).then(function (response) {
                // the server disagreed about who is asking
                if (response.status === 401) {
                    return response.clone().json().catch(function () { return {}; }).then(function (body) {
                        if (body && body.error === 'session_expired') throw sessionExpired();
                        return response;
                    });
                }

                if (response.status !== 419 || isRetry) return response;

                return refresh().then(function (data) {
                    /* Signed in a moment ago, a stranger now: the retry would go
                       through as a guest and the visitor would never know. */
                    if (window.IS_LOGGED_IN && !data.authenticated) throw sessionExpired();
                    return send(true);
                });
            });
        }
    }

    /* Coming back to a tab that has been in the background for a while: refresh
       before anything is clicked. This is also the only fix available to the
       plain form posts — the sign-in form, the newsletter — which cannot be
       replayed from JavaScript once the browser has taken them. */
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
            hiddenAt = Date.now();
            return;
        }

        if (hiddenAt && Date.now() - hiddenAt > STALE_AFTER_MS) refresh().catch(function () {});
        hiddenAt = 0;
    });

    window.csrfToken = token;
    window.csrfRefresh = refresh;
    window.csrfReady = ready;
    window.csrfFetch = csrfFetch;
}());
